import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography, Grid, Card, CardContent, alpha, useTheme } from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import InventoryIcon from '@mui/icons-material/Inventory';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const FeaturesSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const features = [
    {
      icon: <ReceiptLongIcon sx={{ fontSize: 48 }} />,
      title: t('peppol_invoicing_title'),
      description: t('peppol_invoicing_desc'),
      color: theme.palette.primary.main,
    },
    {
      icon: <StoreIcon sx={{ fontSize: 48 }} />,
      title: t('multi_store_support_title'),
      description: t('multi_store_support_desc'),
      color: theme.palette.success.main,
    },
    {
      icon: <PointOfSaleIcon sx={{ fontSize: 48 }} />,
      title: t('integrated_pos_title'),
      description: t('integrated_pos_desc'),
      color: theme.palette.warning.main,
    },
    {
      icon: <InventoryIcon sx={{ fontSize: 48 }} />,
      title: t('inventory_management_title'),
      description: t('inventory_management_desc'),
      color: theme.palette.info.main,
    },
    {
      icon: <CloudSyncIcon sx={{ fontSize: 48 }} />,
      title: t('online_store_title'),
      description: t('online_store_desc'),
      color: theme.palette.secondary.main,
    },
    {
      icon: <ShoppingCartIcon sx={{ fontSize: 48 }} />,
      title: t('ecommerce_title'),
      description: t('ecommerce_desc'),
      color: theme.palette.error.main,
    },
  ];

  return (
    <Box sx={{ py: 10, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: theme.palette.text.primary,
            }}
          >
            {t('features_section_title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 700,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {t('features_section_subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 24px ${alpha(feature.color, 0.2)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(feature.color, 0.1),
                      color: feature.color,
                      mb: 3,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
